import { logEvent, type ActionEventType } from './eventLogger';

/**
 * Dispatch strategies for in-editor action logging.
 *
 * Editor actions are far higher-frequency than the funnel events in eventLogger, so
 * they are not all sent one-request-per-occurrence:
 *
 * - `trackAction`      — one request per occurrence. For discrete, deliberate acts
 *                        (drawing a shape, placing an image, adding a page).
 * - `trackBurstAction` — accumulates and flushes a single request per BURST_WINDOW_MS
 *                        carrying an occurrence `count`. For stroke-level tools, where
 *                        a minute of drawing would otherwise be hundreds of requests.
 * - `trackActionOnce`  — logs the first occurrence for a given target and stays quiet
 *                        until the target changes. For "user dragged this text block",
 *                        which should count sessions rather than intermediate moves.
 * - `trackTrailingAction` — holds the latest trace for a target and sends it once the
 *                        action stops arriving. For continuous controls (a color drag,
 *                        a size stepper) where only the value settled on is the choice.
 */

/** Window over which repeated burst actions are coalesced into one request. */
export const BURST_WINDOW_MS = 10_000;

/** Quiet period a continuous control must reach before its settled value is sent. */
export const TRAILING_WINDOW_MS = 2_000;

export type ActionTrace = Record<string, unknown>;

interface PendingBurst {
  count: number;
  /** Most recent trace for this action; earlier ones in the window are dropped. */
  trace: ActionTrace;
}

interface PendingTrailing {
  eventType: ActionEventType;
  /** Latest trace for this target; the earlier ones are what the throttling discards. */
  trace: ActionTrace;
  timer: ReturnType<typeof setTimeout>;
}

const pendingBursts = new Map<ActionEventType, PendingBurst>();
/** Per-scope memory of the last target logged, so `trackActionOnce` can detect a change. */
const lastTargetByScope = new Map<string, string>();
/** Per-target trailing sends, keyed so independent targets never swallow one another. */
const pendingTrailing = new Map<string, PendingTrailing>();

let burstTimer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;

/**
 * Context that is not reachable from the state layer (currently only the mobile/desktop
 * split, which comes from a MUI media-query hook). Merged into every action's trace.
 */
let ambientContext: ActionTrace = {};

export const setActionTrackingContext = (context: ActionTrace): void => {
  ambientContext = context;
};

const send = (eventType: ActionEventType, trace: ActionTrace): void => {
  logEvent({ logType: 'ACTION', eventType, trace: { ...ambientContext, ...trace } });
};

export const flushBurstActions = (): void => {
  if (burstTimer !== null) {
    clearTimeout(burstTimer);
    burstTimer = null;
  }

  pendingBursts.forEach(({ count, trace }, eventType) => send(eventType, { ...trace, count }));
  pendingBursts.clear();
};

/** Sends every trailing action still waiting for its quiet period, latest trace each. */
export const flushTrailingActions = (): void => {
  pendingTrailing.forEach(({ eventType, trace, timer }) => {
    clearTimeout(timer);
    send(eventType, trace);
  });
  pendingTrailing.clear();
};

const flushPendingActions = (): void => {
  flushBurstActions();
  flushTrailingActions();
};

/**
 * Flush anything still pending when the page goes away, otherwise the tail of every
 * drawing session is lost. `visibilitychange` is the reliable signal on mobile Safari,
 * where `pagehide`/`beforeunload` are not guaranteed to fire.
 */
const attachFlushListeners = (): void => {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;

  window.addEventListener('pagehide', flushPendingActions);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPendingActions();
  });
};

export const trackAction = (eventType: ActionEventType, trace: ActionTrace = {}): void => {
  send(eventType, trace);
};

export const trackBurstAction = (eventType: ActionEventType, trace: ActionTrace = {}): void => {
  attachFlushListeners();

  const pending = pendingBursts.get(eventType);
  pendingBursts.set(eventType, { count: (pending?.count ?? 0) + 1, trace });

  // One timer for all burst types: the window starts at the first accumulated action
  // and flushes everything pending, rather than staggering a timer per event type.
  if (burstTimer === null) {
    burstTimer = setTimeout(flushBurstActions, BURST_WINDOW_MS);
  }
};

/**
 * Logs only when `targetId` differs from the last target logged in this scope, so a
 * long editing session on one object produces a single row. Scope keeps independent
 * memories for actions that share an event type (EDIT_TEXT typing vs. moving).
 */
export const trackActionOnce = (
  eventType: ActionEventType,
  scope: string,
  targetId: string,
  trace: ActionTrace = {},
): void => {
  const key = `${eventType}#${scope}`;
  if (lastTargetByScope.get(key) === targetId) return;

  lastTargetByScope.set(key, targetId);
  send(eventType, trace);
};

/**
 * Holds the latest trace for a target and sends it only once the action stops arriving.
 *
 * A continuous control fires on every intermediate value — a color picker dispatches
 * throughout the drag — but only the value it settles on is a choice the user made. The
 * timer restarts on each call, so a gesture produces one request carrying its final
 * state. Keying by target keeps concurrent subjects apart: styling a second block starts
 * its own pending send rather than replacing the first block's.
 */
export const trackTrailingAction = (
  eventType: ActionEventType,
  scope: string,
  targetId: string,
  trace: ActionTrace = {},
): void => {
  attachFlushListeners();

  const key = `${eventType}#${scope}#${targetId}`;
  const pending = pendingTrailing.get(key);
  if (pending) clearTimeout(pending.timer);

  const timer = setTimeout(() => {
    const settled = pendingTrailing.get(key);
    pendingTrailing.delete(key);
    if (settled) send(settled.eventType, settled.trace);
  }, TRAILING_WINDOW_MS);

  pendingTrailing.set(key, { eventType, trace, timer });
};

/** Drops all pending and remembered state. Called when a new document is loaded. */
export const resetActionTracking = (): void => {
  if (burstTimer !== null) {
    clearTimeout(burstTimer);
    burstTimer = null;
  }
  pendingBursts.clear();
  lastTargetByScope.clear();
  pendingTrailing.forEach(({ timer }) => clearTimeout(timer));
  pendingTrailing.clear();
};
